import { useState, useEffect } from 'react'
import { Plus, CheckCircle } from 'lucide-react'
import api from '../../services/api'

const nuAlsDatetimeLocal = () => {
  const nu = new Date()
  nu.setSeconds(0, 0)
  return nu.toISOString().slice(0, 16)
}

/**
 * UrenRegistratieSectie — Inline urenregistratie voor installatiejobs.
 * Maakt TimeLog records aan via POST /mobile/worker/jobs/{id}/uren.
 */
export default function UrenRegistratieSectie({ jobId, projectId }) {
  const [toonForm, setToonForm] = useState(false)
  const [startTijd, setStartTijd] = useState('')
  const [eindTijd, setEindTijd] = useState('')
  const [activityType, setActivityType] = useState('ASSEMBLY')
  const [opmerking, setOpmerking] = useState('')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState(null)
  const [succes, setSucces] = useState(null)

  useEffect(() => {
    if (toonForm) setEindTijd(nuAlsDatetimeLocal())
  }, [toonForm])

  const handleSubmit = async () => {
    if (!startTijd || !eindTijd) {
      setFout('Start- en eindtijd zijn verplicht')
      return
    }
    if (new Date(eindTijd) <= new Date(startTijd)) {
      setFout('Eindtijd moet na starttijd liggen')
      return
    }
    setBezig(true)
    setFout(null)
    try {
      const res = await api.post(`/mobile/worker/jobs/${jobId}/uren`, {
        start_tijd: new Date(startTijd).toISOString(),
        eind_tijd: new Date(eindTijd).toISOString(),
        activity_type: activityType,
        opmerking: opmerking.trim() || null,
      })
      const uren = res.data.duur_uren
      setSucces(`${uren} uur geregistreerd`)
      setToonForm(false)
      setStartTijd('')
      setEindTijd('')
      setOpmerking('')
      setTimeout(() => setSucces(null), 3000)
    } catch (e) {
      setFout(e.response?.data?.detail ?? 'Registratie mislukt')
    } finally {
      setBezig(false)
    }
  }

  const duurUren = startTijd && eindTijd && new Date(eindTijd) > new Date(startTijd)
    ? ((new Date(eindTijd) - new Date(startTijd)) / 3600000).toFixed(2)
    : null

  return (
    <div className="bg-white rounded-lg shadow border border-line dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Uren</h2>
        {!toonForm && (
          <button
            onClick={() => setToonForm(true)}
            className="flex items-center gap-1 text-sm text-blue-600 font-medium"
          >
            <Plus size={16} /> Registreren
          </button>
        )}
      </div>

      {succes && (
        <div className="bg-green-50 rounded-lg px-4 py-3 mb-3 flex items-center gap-2 text-green-800 text-sm">
          <CheckCircle size={16} />
          {succes}
        </div>
      )}

      {toonForm && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Starttijd</label>
            <input
              type="datetime-local"
              value={startTijd}
              onChange={e => setStartTijd(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Eindtijd</label>
            <input
              type="datetime-local"
              value={eindTijd}
              onChange={e => setEindTijd(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {duurUren && (
            <p className="text-xs text-blue-600 font-medium">
              {'\u23F1'} {duurUren} uur
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type werk</label>
            <select
              value={activityType}
              onChange={e => setActivityType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="ASSEMBLY">Assemblage / Plaatsing</option>
              <option value="FINISHING">Afwerking</option>
              <option value="CLEANING">Opruimen</option>
              <option value="MEETING">Overleg</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Opmerking (optioneel)</label>
            <input
              type="text"
              value={opmerking}
              onChange={e => setOpmerking(e.target.value)}
              placeholder="bv. keuken rechts afgewerkt"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {fout && <p className="text-red-500 text-xs">{fout}</p>}

          <div className="flex gap-2">
            <button
              onClick={() => { setToonForm(false); setFout(null) }}
              className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600"
            >
              Annuleer
            </button>
            <button
              onClick={handleSubmit}
              disabled={bezig}
              className="flex-1 py-2 bg-blue-600 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium"
            >
              {bezig ? 'Bezig\u2026' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
